import React, { useState, useEffect } from 'react';
import './ElectoratePrediction.css';
import VoteBar from './VoteBar';

// Import all state prediction files
import actPredictions from '../data/predictions/predictions2025_act.json';
import nswPredictions from '../data/predictions/predictions2025_nsw.json';
import ntPredictions from '../data/predictions/predictions2025_nt.json';
import qldPredictions from '../data/predictions/predictions2025_qld.json';
import saPredictions from '../data/predictions/predictions2025_sa.json';
import tasPredictions from '../data/predictions/predictions2025_tas.json';
import vicPredictions from '../data/predictions/predictions2025_vic.json';
import waPredictions from '../data/predictions/predictions2025_wa.json';

const ElectoratePrediction = ({ electorateName }) => {
  const [prediction, setPrediction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isACT, setIsACT] = useState(false);
  const [isNT, setIsNT] = useState(false);
  const [isTAS, setIsTAS] = useState(false);
  const [isSA, setIsSA] = useState(false);
  const [isWA, setIsWA] = useState(false);
  const [isQLD, setIsQLD] = useState(false);
  const [isVIC, setIsVIC] = useState(false);
  const [isNSW, setIsNSW] = useState(false);

  useEffect(() => {
    try {
      // Combine all state predictions
      const allPredictions = [
        ...actPredictions,
        ...nswPredictions,
        ...ntPredictions,
        ...qldPredictions,
        ...saPredictions,
        ...tasPredictions,
        ...vicPredictions,
        ...waPredictions
      ];

      // Find the prediction for the specified electorate
      const electoratePrediction = allPredictions.find(
        pred => pred.electorateName === electorateName
      );

      if (!electoratePrediction) {
        throw new Error(`No prediction found for electorate: ${electorateName}`);
      }

      // Check if the electorate is in ACT, NT, TAS, SA, WA, QLD, VIC, or NSW
      setIsACT(actPredictions.some(pred => pred.electorateName === electorateName));
      setIsNT(ntPredictions.some(pred => pred.electorateName === electorateName));
      setIsTAS(tasPredictions.some(pred => pred.electorateName === electorateName));
      setIsSA(saPredictions.some(pred => pred.electorateName === electorateName));
      setIsWA(waPredictions.some(pred => pred.electorateName === electorateName));
      setIsQLD(qldPredictions.some(pred => pred.electorateName === electorateName));
      setIsVIC(vicPredictions.some(pred => pred.electorateName === electorateName));
      setIsNSW(nswPredictions.some(pred => pred.electorateName === electorateName));
      
      setPrediction(electoratePrediction);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  }, [electorateName]);

  if (loading) return <div className="loading">Loading prediction...</div>;
  if (error) return <div className="error">Error: {error}</div>;
  if (!prediction) return <div className="no-data">No prediction available</div>;

  const { confidence, analysis, projectedSwing } = prediction.prediction;
  
  // Find the top two candidates by projected votes for 2PP display (for standard format electorates)
  const sortedCandidates = [...prediction.candidates].sort((a, b) => b.projectedVotes - a.projectedVotes);
  const [firstCandidate, secondCandidate] = sortedCandidates;

  // Check if we should use the new format (ACT, NT, TAS, SA, WA, QLD, VIC, or NSW)
  const useNewFormat = isACT || isNT || isTAS || isSA || isWA || isQLD || isVIC || isNSW;

  // For electorates using new format, get the 2PP projected results
  const tppCandidates = useNewFormat && prediction.prediction["2ppProjected"] 
    ? prediction.prediction["2ppProjected"].candidates
    : null;

  const getConfidenceColor = (confidence) => {
    switch (confidence.toLowerCase()) {
      case 'high':
        return '#28a745';
      case 'medium':
        return '#ffc107';
      case 'low':
        return '#dc3545';
      default:
        return '#6c757d';
    }
  };

  const formatSwing = (swing) => {
    const sign = swing >= 0 ? '+' : '';
    return `${sign}${swing.toFixed(1)}%`;
  };

  // Get party color, including CLP in Coalition blue
  const getPartyColor = (partyCode) => {
    switch (partyCode) {
      case 'LP':
      case 'LNP':
      case 'CLP':
      case 'NP':
        return '#1E3D8F'; // Coalition blue
      case 'ALP':
        return '#E13940'; // Labor red
      case 'GRN':
        return '#2E8B57'; // Greens green
      case 'IND':
        return '#800080'; // Independent purple
      case 'KAP':
        return '#FFD700'; // KAP gold/brown
      case 'CA':
        return '#FFA500'; // Centre Alliance orange
      case 'ONP':
        return '#FF4500'; // One Nation orange-red
      default:
        return '#6c757d'; // Default gray
    }
  };

  return (
    <div className="electorate-prediction">
      <div className="prediction-analysis">
        {analysis}
      </div>
      
      <div className="prediction-details">
        <div className="prediction-meta">
          <h3>2025 Prediction</h3>
          <div className="prediction-header">
            <div className="confidence-indicator" style={{ backgroundColor: getConfidenceColor(confidence) }}>
              {confidence} Confidence
            </div>
            <div className="swing-indicator">
              Projected Swing: {formatSwing(projectedSwing)}
            </div>
          </div>
        </div>

        <div className="prediction-section">
          <h3>Projected Two-Party Preferred</h3>
          {useNewFormat && tppCandidates ? (
            // For ACT/NT/TAS/SA/WA/QLD/VIC/NSW electorates, use the 2ppProjected data
            <VoteBar
              votes={tppCandidates[0].projectedVotes}
              otherVotes={tppCandidates[1].projectedVotes}
              partyCode={tppCandidates[0].partyCode}
              otherPartyCode={tppCandidates[1].partyCode}
              showVotes={false}
              partyColor={getPartyColor(tppCandidates[0].partyCode)}
              otherPartyColor={getPartyColor(tppCandidates[1].partyCode)}
            />
          ) : (
            // For other states, use the standard display
            <VoteBar
              votes={firstCandidate.projectedVotes}
              otherVotes={secondCandidate.projectedVotes}
              partyCode={firstCandidate.partyCode}
              otherPartyCode={secondCandidate.partyCode}
              showVotes={false}
              partyColor={getPartyColor(firstCandidate.partyCode)}
              otherPartyColor={getPartyColor(secondCandidate.partyCode)}
            />
          )}
        </div>

        <div className="candidates-list">
          <h4>Candidates</h4>
          {prediction.candidates.map((candidate, index) => (
            <div key={`${candidate.partyCode}-${index}`} className="candidate">
              <div className="candidate-name">
                {candidate.givenNames} {candidate.familyName}
                {candidate.isIncumbent && <span className="incumbent-tag">Incumbent</span>}
              </div>
              <div className="candidate-party" style={{ color: getPartyColor(candidate.partyCode) }}>
                {candidate.partyCode}
              </div>
              {useNewFormat && (
                <div className="projected-votes">
                  {candidate.projectedVotes.toFixed(1)}%
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ElectoratePrediction; 